import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import Slide from '@mui/material/Slide'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import Collapse from '@mui/material/Collapse'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { fetchTerritoryPerformance } from '@user/store/territory/api'
import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  TerritoryOwnerLoad,
  TerritoryPerformance,
  TerritoryPerformanceReport,
  TerritoryTeamPerformance,
} from '@user/store/territory/typings'
import { money } from './territoryMetrics'

/**
 * Presets rather than two date pickers: "how did we do last quarter" is the question, and a
 * picker makes the user answer a harder one first.
 *
 * Rolling windows AND calendar periods, because a performance review asks both kinds of
 * question -- "how are we doing lately" and "how did last year go". Rolling-only presets
 * cannot reach a finished period at all, which is how a first version of this showed no value
 * anywhere: every window ended after the most recent closed deal.
 */
type Range = {
  key: string
  label: string
  resolve: () => { from: Date; to: Date }
}

const startOfYear = (y: number) => new Date(Date.UTC(y, 0, 1))
const endOfYear = (y: number) => new Date(Date.UTC(y, 11, 31, 23, 59, 59))
const daysAgo = (n: number) => new Date(Date.now() - n * 24 * 60 * 60 * 1000)

const RANGES: Range[] = [
  {
    key: 'd7',
    label: 'Last 7 days',
    resolve: () => ({ from: daysAgo(7), to: new Date() }),
  },
  {
    key: 'd30',
    label: 'Last 30 days',
    resolve: () => ({ from: daysAgo(30), to: new Date() }),
  },
  {
    key: 'd90',
    label: 'Last 90 days',
    resolve: () => ({ from: daysAgo(90), to: new Date() }),
  },
  {
    key: 'ytd',
    label: 'This year',
    resolve: () => ({
      from: startOfYear(new Date().getUTCFullYear()),
      to: new Date(),
    }),
  },
  {
    key: 'lastyear',
    label: 'Last year',
    resolve: () => {
      const y = new Date().getUTCFullYear() - 1
      return { from: startOfYear(y), to: endOfYear(y) }
    },
  },
  {
    key: 'all',
    label: 'All time',
    resolve: () => ({ from: new Date(Date.UTC(2000, 0, 1)), to: new Date() }),
  },
]

const DEFAULT_RANGE = 'd30'

type Props = {
  show: boolean
  onClose: () => void
  /** Moves the map to a territory, so a row can be read against the shape it describes. */
  onZoomTo: (territoryId: number) => void
}

/**
 * Sized so the five metric columns total 250px, leaving the territory name ~230px at the
 * drawer width below -- enough for "Monmouth South" and a potential line without clipping.
 */
const COUNT_W = 44
const MONEY_W = 59
/** Keeps the name readable instead of collapsing to an ellipsis as columns were added. */
const NAME_MIN_W = 150

/** A count. Zero is dimmed so a populated row reads at a glance. */
const Count: FC<{ value: number }> = ({ value }) => (
  <Typography
    variant="body2"
    sx={{
      width: COUNT_W,
      textAlign: 'right',
      color: value ? 'text.primary' : 'text.disabled',
    }}
  >
    {value}
  </Typography>
)

/**
 * A money cell, rendered even at zero.
 *
 * An earlier version hid $0 to reduce noise, which removed the column entirely whenever no
 * record in the period had products -- so the figures looked missing rather than zero, and
 * there was no way to tell the feature existed at all. An explicit $0 is information; a blank
 * space is a question.
 */
const Money: FC<{ value: number }> = ({ value }) => (
  <Typography
    variant="body2"
    sx={{
      width: MONEY_W,
      textAlign: 'right',
      color: value ? 'success.main' : 'text.disabled',
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {money(value)}
  </Typography>
)

const Row: FC<{
  row: TerritoryPerformance
  expanded: boolean
  onToggle: () => void
  onZoomTo?: () => void
}> = ({ row, expanded, onToggle, onZoomTo }) => {
  const hasOwners = row.owners.length > 0

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 2,
          cursor: hasOwners || onZoomTo ? 'pointer' : 'default',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => {
          // Clicking the row does the two useful things at once: moves the map to the
          // territory and opens its per-person breakdown.
          onZoomTo?.()
          if (hasOwners) onToggle()
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '2px',
            flexShrink: 0,
            bgcolor: row.territoryId
              ? (row.colorHex ?? DEFAULT_TERRITORY_COLOR)
              : 'transparent',
            border: row.territoryId ? undefined : '1px dashed',
            borderColor: 'text.disabled',
          }}
        />

        <Box sx={{ flex: 1, minWidth: NAME_MIN_W, overflow: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: row.territoryId ? 400 : 600,
              fontStyle: row.territoryId ? undefined : 'italic',
            }}
          >
            {row.territoryName}
          </Typography>

          {/* Potential, under the name rather than as two more columns: it is context for
              reading the activity, not another score. Without it a zero is ambiguous --
              an empty territory and a neglected one look identical. */}
          {!!row.territoryId && (
            <Typography variant="caption" color="text.secondary">
              {Math.round(row.areaSquareMiles)} mi&sup2; &middot;{' '}
              {row.contacts === 1 ? '1 contact' : `${row.contacts} contacts`}
              {row.contacts === 0 && ' \u2014 nothing here yet'}
            </Typography>
          )}
        </Box>

        <Count value={row.leads} />
        <Money value={row.leadValue} />
        <Count value={row.visits} />
        <Count value={row.closes} />
        <Money value={row.closedValue} />

        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
          {hasOwners &&
            (expanded ? (
              <ExpandLessIcon fontSize="small" color="action" />
            ) : (
              <ExpandMoreIcon fontSize="small" color="action" />
            ))}
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        {row.owners.map((owner) => (
          <Box
            key={owner.ownerId ?? 'unassigned'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              pl: 5,
              pr: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color={
                  owner.isTerritoryOwner ? 'text.primary' : 'text.secondary'
                }
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: owner.isTerritoryOwner ? 600 : 400,
                }}
              >
                {owner.ownerName}
              </Typography>
              {/* Marks the people responsible for this area. Without it an assigned owner
                  showing zeroes is indistinguishable from a passer-by showing zeroes --
                  and only one of those is a problem. */}
              {owner.isTerritoryOwner && (
                <Chip
                  label="Owner"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 16,
                    fontSize: 10,
                    '& .MuiChip-label': { px: 0.6 },
                  }}
                />
              )}
            </Box>
            <Count value={owner.leads} />
            <Money value={owner.leadValue} />
            <Count value={owner.visits} />
            <Count value={owner.closes} />
            <Money value={owner.closedValue} />
            <Box sx={{ width: 24 }} />
          </Box>
        ))}
      </Collapse>
    </>
  )
}

/**
 * One sales team's territories, with the team's own totals in the header.
 *
 * A card per team rather than one flat list: a manager reviews by the team responsible, and the
 * subtotal is usually the number they came for. A flat list also stops being readable well
 * before the thousand territories the NFR allows.
 */
const TeamCard: FC<{
  team: TerritoryTeamPerformance
  expanded: string | null
  onToggle: (key: string | null) => void
  onZoomTo: (territoryId: number) => void
}> = ({ team, expanded, onToggle, onZoomTo }) => (
  <Card variant="outlined" sx={{ m: 1.5, overflow: 'visible' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: 'action.hover',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: NAME_MIN_W, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {team.salesTeamName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {Math.round(team.areaSquareMiles)} mi&sup2; &middot; {team.contacts}{' '}
          contacts
        </Typography>
      </Box>
      <Count value={team.leads} />
      <Money value={team.leadValue} />
      <Count value={team.visits} />
      <Count value={team.closes} />
      <Money value={team.closedValue} />
      <Box sx={{ width: 24 }} />
    </Box>

    {team.territories.map((row) => (
      <Row
        key={row.territoryId}
        row={row}
        expanded={expanded === String(row.territoryId)}
        onToggle={() =>
          onToggle(
            expanded === String(row.territoryId)
              ? null
              : String(row.territoryId),
          )
        }
        onZoomTo={
          row.territoryId ? () => onZoomTo(row.territoryId!) : undefined
        }
      />
    ))}
  </Card>
)

/**
 * One person's row in the by-owner view.
 *
 * Responsibility on the left, activity on the right, with the gap between them left visible
 * rather than averaged away. Two rows matter most and neither is obvious in the by-territory
 * view: an owner holding ground they are not working, and someone working ground they do not
 * own. On the data this was built against, seven people were in the second group.
 */
const OwnerRowView: FC<{ row: TerritoryOwnerLoad }> = ({ row }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 1,
      px: 2,
      borderBottom: 1,
      borderColor: 'divider',
      opacity: row.isGap ? 0.85 : 1,
    }}
  >
    <Box sx={{ flex: 1, minWidth: NAME_MIN_W, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          noWrap
          fontWeight={row.isGap ? 600 : 400}
          fontStyle={row.isGap ? 'italic' : undefined}
        >
          {row.ownerName}
        </Typography>
        {row.worksWithoutTerritory && (
          <Tooltip title="Doing work but owns no territory — a candidate to be given one">
            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main' }} />
          </Tooltip>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" noWrap>
        {row.territoryCount === 0
          ? 'no territory'
          : `${row.territoryCount === 1 ? '1 territory' : `${row.territoryCount} territories`} · ${row.contacts} contacts · ${Math.round(row.areaSquareMiles)} mi²`}
        {row.hasSharedTerritory && ' (shared)'}
      </Typography>
    </Box>

    <Count value={row.leads} />
    <Money value={row.leadValue} />
    <Count value={row.visits} />
    <Count value={row.closes} />
    <Money value={row.closedValue} />
    <Box sx={{ width: 24 }} />
  </Box>
)

/**
 * Leads, visits and closes by territory, as a slideout over the map (WWM-17508).
 *
 * Answers the charter's manager question: "when I review performance, I want to see leads,
 * visits and closes by territory."
 *
 * A slideout rather than a settings page precisely because it sits over the map: clicking a row
 * flies to that territory, so the number and the shape it describes are on screen together.
 * That is the thing a separate reporting page cannot do.
 *
 * Owners are credited BY GEOCODE from each record's own assignee, so a rep working inside
 * someone else's territory shows up there rather than being quietly folded into the territory's
 * configured owner.
 */
export const TerritoryPerformanceDrawer: FC<Props> = ({
  show,
  onClose,
  onZoomTo,
}) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [rangeKey, setRangeKey] = useState<string>(DEFAULT_RANGE)
  const [report, setReport] = useState<TerritoryPerformanceReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  // "Which area is doing well" and "is each person carrying a fair load" are different
  // questions over the same data, and rebalancing asks the second one.
  const [view, setView] = useState<'territory' | 'owner'>('territory')

  // Which request's answer we are still waiting for. Switching range fires a new load without
  // cancelling the old one, and the ranges differ enormously in cost -- "All time" scans far more
  // than "Last 7 days" -- so leaving for a shorter range could let the heavier earlier response
  // land second and paint its numbers under the newer range's label. Nothing in the UI would
  // suggest the figures belonged to a different period.
  const latestRequestRef = useRef(0)

  const load = useCallback(
    async (key: string) => {
      const requestId = ++latestRequestRef.current

      setLoading(true)
      setError(null)

      const range = RANGES.find((r) => r.key === key) ?? RANGES[1]
      const { from, to } = range.resolve()

      const result = await dispatch(
        fetchTerritoryPerformance({
          from: from.toISOString(),
          to: to.toISOString(),
        }),
      )

      // Superseded while we were waiting: a newer load owns the UI now, and this one must not
      // touch it -- not the report, not the error, and not the spinner, which belongs to the
      // request still running.
      if (requestId !== latestRequestRef.current) return

      setLoading(false)

      if (fetchTerritoryPerformance.rejected.match(result)) {
        setError(result.payload?.message ?? 'Could not load the report.')
        setReport(null)
        return
      }

      setReport(result.payload as TerritoryPerformanceReport)
    },
    [dispatch],
  )

  // Reloaded on open as well as on range change: territories are redrawn constantly and the
  // figures are computed live, so a report left open from earlier would be describing polygons
  // that no longer exist.
  useEffect(() => {
    if (!show) return
    load(rangeKey)
  }, [show, rangeKey, load])

  const teams = useMemo(() => report?.teams ?? [], [report])

  const hasAnything =
    teams.some((t) => t.leads || t.visits || t.closes) ||
    !!(
      report?.unterritoried.leads ||
      report?.unterritoried.visits ||
      report?.unterritoried.closes
    )

  return (
    <Slide direction="right" in={show} mountOnEnter unmountOnExit>
      <Card
        sx={{
          width: isMobile ? '100vw' : 640,
          position: 'fixed',
          top: isMobile ? '64px' : 0,
          left: isMobile ? 0 : '72px',
          bottom: 0,
          zIndex: 1300,
          boxShadow: 4,
          borderRadius: undefined,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
        }}
        data-test-id="territory-performance-drawer"
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Territory performance
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}
        >
          <TextField
            select
            size="small"
            sx={{ flex: 1 }}
            label="Period"
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value)}
            data-test-id="territory-performance-range"
          >
            {RANGES.map((r) => (
              <MenuItem key={r.key} value={r.key}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, next) => next && setView(next)}
            data-test-id="territory-performance-view"
          >
            <ToggleButton
              value="territory"
              sx={{ px: 1.5, textTransform: 'none' }}
            >
              Areas
            </ToggleButton>
            <ToggleButton value="owner" sx={{ px: 1.5, textTransform: 'none' }}>
              People
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ width: 10 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, minWidth: NAME_MIN_W }}
          >
            {view === 'owner' ? 'Person' : 'Territory'}
          </Typography>
          {[
            // Explained on hover, because these are terms of art. Leads -> Pipeline is what
            // came in and what it might be worth; Closes -> Won is what converted and what it
            // paid; Visits is the effort behind both. The caveats are stated rather than
            // assumed known: both money columns are FIRST-YEAR value, and Pipeline is
            // unweighted, so a reader who takes them at face value would overstate the result.
            {
              label: 'Leads',
              w: COUNT_W,
              help: 'New leads created in this period whose address falls inside the territory.',
            },
            {
              label: 'Pipeline',
              w: MONEY_W,
              help: 'What those new leads could be worth: the first-year price of the services attached to them. Not weighted by how likely they are to close, and often $0 because services are usually added later as the deal develops.',
            },
            {
              label: 'Visits',
              w: COUNT_W,
              help: 'Knocks. One for each time someone recorded a knock status at a door, so knocking the same door twice counts twice. Doors with no lead on them are included.',
            },
            {
              label: 'Closes',
              w: COUNT_W,
              help: 'Opportunities won in this period. Closed/Lost and Disqualified are not counted.',
            },
            {
              label: 'Won',
              w: MONEY_W,
              help: 'Money actually taken from those wins: the first-year value of the services sold. A multi-year contract shows only its first year.',
            },
          ].map((h) => (
            <Tooltip key={h.label} title={h.help} placement="top" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  width: h.w,
                  textAlign: 'right',
                  cursor: 'help',
                  textDecoration: 'underline dotted',
                  textUnderlineOffset: 3,
                }}
              >
                {h.label}
              </Typography>
            </Tooltip>
          ))}
          <Box sx={{ width: 24 }} />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && report && (
            <>
              {report.isTruncated && (
                <Alert severity="warning" sx={{ m: 2 }}>
                  There was more activity than this report can count. These are
                  minimums, not totals.
                </Alert>
              )}

              {!hasAnything && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2 }}
                >
                  No leads, visits or closes in this period.
                </Typography>
              )}

              {view === 'owner'
                ? report.ownerLoad.map((row) => (
                    <OwnerRowView
                      key={row.ownerId ?? row.ownerName}
                      row={row}
                    />
                  ))
                : teams.map((team) => (
                    <TeamCard
                      key={team.salesTeamId}
                      team={team}
                      expanded={expanded}
                      onToggle={setExpanded}
                      onZoomTo={onZoomTo}
                    />
                  ))}

              {view === 'territory' && report.unterritoried && (
                <>
                  <Divider sx={{ my: 1 }} />
                  {/* Kept visible even at zero. "Nothing happened outside your territories"
                      is a real answer to the coverage question, and hiding the row would
                      leave the manager unable to tell it from the row not existing. */}
                  <Row
                    row={report.unterritoried}
                    expanded={expanded === 'outside'}
                    onToggle={() =>
                      setExpanded(expanded === 'outside' ? null : 'outside')
                    }
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', px: 2, pb: 2, pt: 0.5 }}
                  >
                    Activity outside every territory. A high number here means
                    the work is happening where no territory is drawn.
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>
      </Card>
    </Slide>
  )
}
